import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';

export interface AddressLookupDetails {
    house_number?: string;
    road?: string;
    pedestrian?: string;
    footway?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    state_district?: string;
    state?: string;
    postcode?: string;
    country_code?: string;
    [key: string]: string | undefined;
}

export interface AddressLookupResult {
    place_id: number;
    display_name: string;
    lat: string;
    lon: string;
    address: AddressLookupDetails;
}

@Injectable({providedIn: 'root'})
export class AddressLookupService {

    private readonly endpoint = 'https://nominatim.openstreetmap.org/search';
    private readonly cache = new Map<string, AddressLookupResult[]>();
    private lastRequestAt = 0;

    constructor(private http: HttpClient) {
    }

    async searchItalianAddress(query: string): Promise<AddressLookupResult[]> {
        const normalizedQuery = query.trim().replace(/\s+/g, ' ');
        if (normalizedQuery.length < 5) return [];

        const cacheKey = normalizedQuery.toLocaleLowerCase('it');
        const cached = this.cache.get(cacheKey);
        if (cached) return cached;

        // The public Nominatim service allows at most one request per second.
        const waitMs = Math.max(0, 1000 - (Date.now() - this.lastRequestAt));
        if (waitMs > 0) {
            await new Promise(resolve => setTimeout(resolve, waitMs));
        }

        const params = new HttpParams()
            .set('q', normalizedQuery)
            .set('format', 'jsonv2')
            .set('addressdetails', '1')
            .set('countrycodes', 'it')
            .set('accept-language', 'it')
            .set('limit', '5');

        this.lastRequestAt = Date.now();
        const results = await firstValueFrom(this.http.get<AddressLookupResult[]>(this.endpoint, {params}));
        this.cache.set(cacheKey, results);
        return results;
    }
}

export function streetFromLookup(result: AddressLookupResult): string {
    const address = result.address ?? {};
    return address.road ?? address.pedestrian ?? address.footway ?? result.display_name.split(',')[0];
}

export function cityFromLookup(result: AddressLookupResult): string | undefined {
    const address = result.address ?? {};
    return address.city ?? address.town ?? address.village ?? address.municipality;
}

export function provinceCodeFromLookup(
    result: AddressLookupResult,
    provinces: { id: string; name: string }[]
): string | undefined {
    const address = result.address ?? {};
    const isoProvince = Object.entries(address)
        .find(([key, value]) => key.startsWith('ISO3166-2-lvl') && /^IT-[A-Z]{2}$/i.test(value ?? ''))?.[1]
        ?.split('-')[1]
        ?.toUpperCase();
    if (isoProvince && provinces.some((province) => province.id === isoProvince)) {
        return isoProvince;
    }
    const normalize = (value: string) => value
        .toLocaleLowerCase('it')
        .replace(/provincia (di|del|della) /g, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
    const candidates = [address['county'], address['state_district']]
        .filter((value): value is string => !!value)
        .map(normalize);
    return provinces.find((province) => candidates.includes(normalize(province.name)))?.id;
}
